import React from 'react';
import { Link } from 'react-router-dom';
import { Lock, X, LogIn, UserPlus } from 'lucide-react';
import { ROUTE_NAMES } from '../../../routes/routeConstants';

interface LoginPromptDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  message?: string;
}

const LoginPromptDialog: React.FC<LoginPromptDialogProps> = ({
  isOpen,
  onClose,
  title = "Login Required",
  message = "You need to be logged in to view job details. Please sign in or create an account to continue."
}) => {
  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 transform transition-all">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: 'var(--twc-timberwolf)' }}>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-blue-100">
              <Lock className="w-5 h-5 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold" style={{ color: 'var(--twc-ink)' }}>
              {title}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-gray-600 leading-relaxed mb-6">
            {message}
          </p>

          {/* Actions */}
          <div className="flex flex-col gap-3">
            <Link
              to={ROUTE_NAMES.PAGES.LOGIN}
              onClick={onClose}
              className="flex items-center justify-center gap-2 px-6 py-3 rounded-lg text-white font-medium transition-all hover:opacity-90"
              style={{ backgroundColor: 'var(--twc-cambridge)' }}
            >
              <LogIn className="w-4 h-4" />
              Sign In
            </Link>
            <Link
              to={ROUTE_NAMES.PAGES.REGISTER}
              onClick={onClose}
              className="flex items-center justify-center gap-2 px-6 py-3 rounded-lg border font-medium transition-all hover:bg-gray-50"
              style={{ 
                borderColor: 'var(--twc-timberwolf)', 
                color: 'var(--twc-feldgrau)' 
              }}
            >
              <UserPlus className="w-4 h-4" />
              Create Account
            </Link>
          </div>

          {/* Additional Info */}
          <div className="mt-6 p-4 rounded-lg" style={{ 
            backgroundColor: 'var(--twc-cambridge-light)',
            borderColor: 'var(--twc-cambridge)',
            border: '2px solid'
          }}>
            <p className="text-sm font-bold text-center" style={{color: "var(--twc-feldgrau)"}}>
              <strong>Benefits of creating an account:</strong><br />
              Save jobs, track applications, and get personalized recommendations
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPromptDialog;
